import React, { useState, useEffect } from 'react';
import { Box, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, CircularProgress, Chip, TablePagination, Button, Typography, Grid } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { HousekeepingService } from '../../api/services/housekeepingService';
import TaskTypeFormModal from '../../components/TaskTypeFormModal';
import { extractTotalCount } from '../../components/common/tableStyles';

export default function ConfigurationTab({ tenantId }) {
    const [taskTypes, setTaskTypes] = useState([]);
    const [isLoadingTaskTypes, setIsLoadingTaskTypes] = useState(false);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(20);
    const [total, setTotal] = useState(0);
    const [taskTypeModal, setTaskTypeModal] = useState(false);

    const fetchTaskTypes = async () => {
        setIsLoadingTaskTypes(true);
        try {
            const data = await HousekeepingService.getTaskTypes(tenantId, { page, limit: rowsPerPage });
            const typesArray = data?.data?.types || data?.types || data?.data || data || [];
            setTaskTypes(Array.isArray(typesArray) ? typesArray : []);
            setTotal(extractTotalCount(data, typesArray));
        } catch (error) {
            console.error(error);
        } finally {
            setIsLoadingTaskTypes(false);
        }
    };

    useEffect(() => {
        fetchTaskTypes();
    }, [tenantId, page, rowsPerPage]);

    const handleChangePage = (event, newPage) => setPage(newPage);
    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const paginatedTaskTypes = taskTypes;

    return (
        <Grid container spacing={4}>
            <Grid item xs={12} md={12}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6" fontWeight="bold">Task Types Dictionary</Typography>
                    <Button variant="contained" color="primary" size="small" startIcon={<AddIcon />} onClick={() => setTaskTypeModal(true)} sx={{ borderRadius: 2 }}>
                        Create Task Type
                    </Button>
                </Box>
                <TableContainer component={Paper}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Name</TableCell>
                                <TableCell>Duration</TableCell>
                                <TableCell>Color Tag</TableCell>
                                <TableCell align="right">Status</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {isLoadingTaskTypes ? (
                                <TableRow><TableCell colSpan={4} align="center" sx={{ py: 3 }}><CircularProgress size={24} /></TableCell></TableRow>
                            ) : taskTypes.length === 0 ? (
                                <TableRow><TableCell colSpan={4} align="center" sx={{ py: 3, color: 'text.secondary' }}>No task types configured.</TableCell></TableRow>
                            ) : (
                                paginatedTaskTypes.map(t => (
                                    <TableRow key={t.id} hover>
                                        <TableCell>{t.name}</TableCell>
                                        <TableCell>{t.defaultDuration}m</TableCell>
                                        <TableCell>
                                            <Box sx={{ width: 24, height: 24, borderRadius: 1, bgcolor: t.colorTag || '#ccc' }} />
                                        </TableCell>
                                        <TableCell align="right">
                                            <Chip label={t.isActive ? 'Active' : 'Inactive'} size="small" color={t.isActive ? 'success' : 'default'} />
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                    <TablePagination
                        component="div"
                        count={taskTypes.length === 0 ? 0 : total}
                        page={page}
                        onPageChange={handleChangePage}
                        rowsPerPage={rowsPerPage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                        rowsPerPageOptions={[20, 50, 100]}
                    />
                </TableContainer>
            </Grid>
            <TaskTypeFormModal open={taskTypeModal} onClose={() => setTaskTypeModal(false)} onSuccess={fetchTaskTypes} />
        </Grid>
    );
}
